import { Button, Group, Text, useShowNotification } from '@inflearn/ds-react-next';
import type { MutationOptions } from '@tanstack/react-query';

import { useTranslation } from 'next-i18next';
import { API_ENDPOINT } from '../../resources/@types/apiEndpoint';
import {
  type ClientApiV1CoursesCourseIdLikeUpdateResponse,
  type ClientApiV1CoursesCourseIdLikeUpdateVariables,
  useClientApiV1CoursesCourseIdLikeUpdateMutation,
} from '../../resources/@types/uccSchema';
import { logger } from '../../resources/Logger';
import { MY_URL } from '../constants/urls';

type MutateOptionsType<TContext> = MutationOptions<
  ClientApiV1CoursesCourseIdLikeUpdateResponse,
  unknown,
  ClientApiV1CoursesCourseIdLikeUpdateVariables,
  TContext
>;

export function useCourseCourseIdLikeUpdateMutation<TContext>(
  courseId: number,
  onMutate?: MutateOptionsType<TContext>['onMutate'],
  onError?: MutateOptionsType<TContext>['onError'],
) {
  const { t } = useTranslation();
  const { showNotification } = useShowNotification();

  return useClientApiV1CoursesCourseIdLikeUpdateMutation<TContext>({
    url: API_ENDPOINT.CLIENT_V1_COURSES_COURSEID_LIKE(String(courseId)),
    options: {
      onMutate: (variables) => onMutate?.(variables),
      onSuccess: (_, variables) => {
        if (variables.status === 'UNLIKE') {
          return;
        }

        showNotification({
          type: 'success',
          withCloseButton: true,
          title: (
            <Group justify="space-between" w="100%">
              <Text size="sm" fw={500}>
                {t('좋아요에 추가되었습니다.')}
              </Text>
              <Button
                component="a"
                size="sm"
                radius="md"
                color="dark"
                variant="light"
                href={MY_URL.LIKES()}
                aria-label={t('좋아요한 강의 목록 보러가기')}
              >
                {t('보러가기')}
              </Button>
            </Group>
          ),
        });
      },
      onError: (error, _variables, context) => {
        onError?.(error, _variables, context);
        showNotification({
          type: 'error',
          title: t('좋아요 추가에 실패했습니다.'),
          message: t('네트워크 연결 확인 후 다시 한 번 시도해주세요.'),
        });
        logger.info(
          `[url]: ${API_ENDPOINT.CLIENT_V1_COURSES_COURSEID_LIKE(String(courseId))}, 호출 에러: ${error}`,
        );
      },
    },
  });
}
